import { Button } from 'antd'
import { Badge } from 'designSystem/components/Badge'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMeta,
  ItemTitle,
} from 'designSystem/components/Item'
import { ConfirmButton } from 'features/libra/components/ConfirmButton'
import type { FC } from 'react'

import type { SignoffLock, SignoffStatus } from './signoffState'
import type { SignoffRecord, SignoffSlot } from './signoffTypes'

/** What the current user's signature would record, shown before they sign. */
export interface SignoffPreview {
  name: string
  date: string
}

const LOCK_MESSAGES: Record<SignoffLock, string> = {
  stage: 'Opens when the ticket is in review',
  group: 'Ticket is not assigned to a ticket group',
  role: 'Requires a different role',
}

export interface SignoffCardProps {
  slot: SignoffSlot
  status: SignoffStatus
  /** False when the ticket waives this slot; it stays signable either way. */
  required: boolean
  lock?: SignoffLock
  /** The live signature, when the slot is signed. */
  record?: SignoffRecord
  /** The most recent signature when the slot has been reset. */
  previous?: SignoffRecord
  preview?: SignoffPreview
  loading: boolean
  disabled: boolean
  onSign: () => void
}

/**
 * One signature block. A locked slot states its reason as visible text, not a
 * tooltip — an antd tooltip on a `disabled` button never fires.
 */
export const SignoffCard: FC<SignoffCardProps> = ({
  slot,
  status,
  required,
  lock,
  record,
  previous,
  preview,
  loading,
  disabled,
  onSign,
}) => (
  <Item variant='outline' size='lg'>
    <ItemContent>
      <ItemTitle>{slot.title}</ItemTitle>
      {slot.description && (
        <ItemDescription>{slot.description}</ItemDescription>
      )}
      <ItemMeta>
        {getMetaLine({
          slot,
          status,
          required,
          lock,
          record,
          previous,
          preview,
        })}
      </ItemMeta>
    </ItemContent>
    <ItemActions variant='trailing'>
      <Badge variant={record ? 'default' : 'outlineMuted'}>
        {getStatusLabel(record, required)}
      </Badge>
      {!record &&
        (status === 'available' ? (
          <ConfirmButton
            label='Sign'
            variant='positive'
            size='small'
            dialogTitle={slot.title}
            message={getConfirmMessage(slot, preview)}
            confirmLabel='Sign'
            loading={loading}
            disabled={disabled}
            onConfirm={onSign}
          />
        ) : (
          <Button size='small' disabled>
            Sign
          </Button>
        ))}
    </ItemActions>
  </Item>
)

function getStatusLabel(
  record: SignoffRecord | undefined,
  required: boolean
): string {
  if (record) return 'Signed'
  return required ? 'Pending' : 'Not required'
}

function getMetaLine({
  slot,
  status,
  required,
  lock,
  record,
  previous,
  preview,
}: Pick<
  SignoffCardProps,
  'slot' | 'status' | 'required' | 'lock' | 'record' | 'previous' | 'preview'
>): string {
  if (record) {
    return `${record.userName} · ${slot.dateLabel} ${formatDate(
      record.signedAt
    )}`
  }

  const parts: Array<string | undefined> = []

  // An unsigned slot with history was reset, not never signed — say so in
  // place of the expected signatory.
  if (previous) {
    parts.push(
      `Previously signed by ${previous.userName} · ${formatDate(
        previous.signedAt
      )}`
    )
  }
  if (!required) parts.push('Not required for this ticket')

  if (status === 'available' && preview) {
    parts.push(`Signing as ${preview.name} · ${slot.dateLabel} ${preview.date}`)
  } else {
    if (!previous && required) parts.push(slot.name)
    if (lock) parts.push(LOCK_MESSAGES[lock])
  }

  return parts.filter(Boolean).join(' · ')
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString()
}

function getConfirmMessage(
  slot: SignoffSlot,
  preview: SignoffPreview | undefined
): string {
  const base = `You are signing as ${slot.title}.`
  return preview
    ? `${base} This records ${preview.name} on ${preview.date} against the ticket.`
    : `${base} Your name and the time are recorded on the ticket.`
}
